import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { FilePlus } from 'lucide-react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import logException from '@dashboard/services/logException';
import useProduct from '../useProduct';
import usePage from '@dashboard/hooks/usePage';
import { Badge } from '@dashboard/components/ui/badge';

import FileDownloadsList from '../../../components/FileDownloadsList';

/**
 * The downloads editor without a product behind it: the caller supplies the
 * saved `files` and the three attach/detach/reorder calls. `ProductFilesForm`
 * wires those to a product; the order-bump create form wires them to form state,
 * because a file uploads fine before the product it belongs to exists.
 */
export const FilesEditor = ({
  files: savedFiles = [],
  onAttach,
  onDetach,
  onReorder,
  onEditFile,
  getBadges,
}) => {
  const { uploadFile, deleteFile } = useFileQueries();
  const { sellerId } = useAuth();

  // In-flight uploads that don't exist on the server yet.
  const [uploads, setUploads] = useState([]);

  // Populated file objects we've seen, so a reorder that briefly leaves the
  // cache holding only file ids can still be rendered with full details.
  const knownFilesById = useRef(new Map());
  savedFiles.forEach(file => {
    if (file && typeof file === 'object' && file._id) {
      knownFilesById.current.set(file._id, file);
    }
  });

  const serverFiles = savedFiles
    .map(file => (typeof file === 'string' ? knownFilesById.current.get(file) : file))
    .filter(Boolean);

  const serverFileIds = new Set(serverFiles.map(file => file._id));

  // Keep uploads visible — in-flight ones, and finished ones the server's files
  // array hasn't reflected yet — until the page query catches up and dedupes them.
  const pendingUploads = uploads.filter(upload => !serverFileIds.has(upload._id));

  const files = [...serverFiles, ...pendingUploads];

  const onDrop = useCallback(
    async acceptedFiles => {
      const newUploads = acceptedFiles.map(file =>
        Object.assign(file, {
          key: uniqueId(),
          preview: URL.createObjectURL(file),
          isUploading: true,
          isUploaded: false,
          uploadProgress: 0,
        }),
      );

      setUploads(prev => [...prev, ...newUploads]);

      async function upload(file) {
        try {
          const { data, error } = await uploadFile({
            file,
            bucket: 'files',
            sellerId,
            onProgress: progress => {
              setUploads(prev =>
                prev.map(item =>
                  item === file ? Object.assign(file, { uploadProgress: progress }) : item,
                ),
              );
            },
          });

          if (error) {
            throw error;
          }

          await onAttach(data._id);

          // Replace the placeholder with the saved file. It stays visible (and
          // resolvable through a reorder) until the product's files array reflects it.
          knownFilesById.current.set(data._id, data);
          setUploads(prev => prev.map(item => (item === file ? { ...data } : item)));
        } catch (error) {
          logException({ message: 'Error in uploading download', error });
          setUploads(prev => prev.filter(item => item !== file));
        }
      }

      await Promise.all(newUploads.map(upload));
    },
    [uploadFile, onAttach, sellerId],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  async function onDeleteFile(file) {
    if (file._id) {
      await onDetach(file._id);
      await deleteFile(file._id);
      setUploads(prev => prev.filter(item => item._id !== file._id));
      return;
    }

    setUploads(prev => prev.filter(item => item !== file));
  }

  function onReorderFiles(reorderedFiles) {
    onReorder(reorderedFiles.map(file => file._id).filter(Boolean));
  }

  return (
    <div>
      <Files
        files={files}
        onDeleteFile={onDeleteFile}
        onEditFile={onEditFile}
        onReorderFiles={onReorderFiles}
        getBadges={getBadges}
      />

      <div
        {...getRootProps()}
        className={clsx(
          'transition-colors p-4 border border-dashed border-input rounded-md cursor-pointer bg-background hover:bg-accent/40 hover:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isDragActive && 'border-solid border-ring bg-accent/40',
        )}
      >
        <input {...getInputProps()} />

        <div className="flex items-center justify-start gap-3">
          <div className="flex items-center justify-center h-9 w-9 rounded-md bg-muted text-muted-foreground/70 shrink-0 @max-[14rem]/upload:hidden">
            <FilePlus aria-hidden="true" strokeWidth={1.75} className="h-4 w-4" />
          </div>

          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Upload files</span> or drag and drop
          </div>
        </div>

        <FileContainer isDragActive={isDragActive} />
      </div>
    </div>
  );
};

function Files({ files, onDeleteFile, onEditFile, onReorderFiles, getBadges }) {
  return (
    <FileDownloadsList
      files={files}
      onDeleteFile={onDeleteFile}
      onEditFile={onEditFile}
      onReorderFiles={onReorderFiles}
      getBadges={getBadges}
      deleteDescription="Past customers won't be able to download this file anymore."
    />
  );
}

Files.propTypes = {
  files: PropTypes.array,
  onDeleteFile: PropTypes.func.isRequired,
  onEditFile: PropTypes.func.isRequired,
  onReorderFiles: PropTypes.func.isRequired,
  getBadges: PropTypes.func,
};

function FileContainer({ isDragActive }) {
  if (isDragActive) {
    return <p></p>;
  }

  return <p></p>;
}

FileContainer.propTypes = {
  isDragActive: PropTypes.bool,
};

// `productId` targets a product outside the page (an order bump); omitted, it
// edits the page's own product.
const ProductFilesForm = ({ productId }) => {
  const { refetch: refetchPage } = usePage();
  const { product, updateProduct, attachFile, detachFile } = useProduct(productId);

  if (!product) return null;

  return (
    <FilesEditor
      files={product.files || []}
      onAttach={fileId => attachFile.mutateAsync(fileId)}
      onDetach={fileId => detachFile.mutateAsync(fileId)}
      onReorder={files => updateProduct.mutateAsync({ files })}
      onEditFile={() => {
        // A bump product is not on this page, so there is nothing to refetch —
        // useProduct already invalidates its own cache.
        if (!productId) refetchPage();
      }}
      getBadges={file => {
        const isAttachedToVariantOption = product.variants?.some(variant =>
          variant.options.some(option => option.files?.includes(file._id)),
        );

        if (!isAttachedToVariantOption) {
          return null;
        }

        return (
          <div className="ml-8">
            <Badge variant="secondary">Attached to variant(s)</Badge>
          </div>
        );
      }}
    />
  );
};

ProductFilesForm.propTypes = {
  productId: PropTypes.string,
};

export default ProductFilesForm;
